import { useEffect, useState } from 'react';
import { useTenantConfig } from './useTenantConfig';
import { IDENTITY_QUERY_KEY, useIdentity } from './useIdentity';
import { useQuery } from 'react-query';
import { fetchWrapper } from '../utils';
import { NAVIGATION_ENDPOINTS } from '../constants/urls';
import { useNavigationVariant } from './useNavigationVariant';
import { NavigationOverrides } from '../NavigationContext';
import { useOneIdEnvironment } from './useOneIdEnvironment';

const LIGHTHOUSE_CONFIG_KEY = 'ui.lighthouse.newUI.enabled';
const USER_JOB_DASHBOARD_CONFIG_KEY = 'userJob.dashboard.enabled';
const PAYMENT_LINK_ENABLED_KEY = 'payment.link.enabled';

export const useBillingLighthousePreference = (
  enabled = true,
  identityQueryKey = IDENTITY_QUERY_KEY,
) => {
  const {
    data: identity,
    isLoading: isGettingIdentity,
    isError: isGettingIdentityError,
  } = useIdentity({
    enabled,
    queryKey: identityQueryKey, // This is a separate query key, to avoid isLoading changed to true when the identity query in the main navigation is loading
  });

  const {
    data: configData,
    isLoading: isLighthouseConfigLoading,
    isError: isLighthouseConfigError,
  } = useTenantConfig({
    enabled,
    configKeys: [LIGHTHOUSE_CONFIG_KEY, USER_JOB_DASHBOARD_CONFIG_KEY],
    tenantPropertyKeys: [PAYMENT_LINK_ENABLED_KEY],
  });
  const lightHouseConfig = configData?.[LIGHTHOUSE_CONFIG_KEY] || 'false';
  const isLoading = enabled && (isGettingIdentity || isLighthouseConfigLoading);

  const [isLighthouseConfigEnabled, setIsLighthouseConfigEnabled] =
    useState(false);

  useEffect(() => {
    if (enabled && !isLighthouseConfigLoading) {
      setIsLighthouseConfigEnabled(
        lightHouseConfig === 'true' ||
          identity?.permissions?.includes(
            'permission.EnableNewNavigationHomepage',
          ) ||
          window.localStorage.getItem('Z_IS_LIGHTHOUSE_HOMEPAGE_ENABLED') ===
            'true',
      );
    }
  }, [
    enabled,
    lightHouseConfig,
    isLighthouseConfigLoading,
    identity?.permissions,
  ]);

  const isPaymentLinkEnabled =
    configData?.[PAYMENT_LINK_ENABLED_KEY] === 'true';

  return {
    isLighthouseConfigEnabled,
    isLoading,
    isError: isGettingIdentityError || isLighthouseConfigError,
    tenantId: identity?.tenantId,
    userId: identity?.userProfileId,
    isPaymentLinkEnabled,
  };
};

export interface RevenueLighthousePreferenceResponse {
  zuoraAI: boolean;
  globalNav: boolean;
}

export const useRevenueLighthousePreference = (enabled = true) => {
  const { revenuePrefix } = useOneIdEnvironment();
  const queryKey = ['RevenueLighthousePreference', revenuePrefix];

  const result = useQuery<RevenueLighthousePreferenceResponse>(
    queryKey,
    async () => {
      const url =
        revenuePrefix === '/revpro'
          ? NAVIGATION_ENDPOINTS.REVPRO_LIGHTHOUSE_PREFERENCE
          : NAVIGATION_ENDPOINTS.REVENUE_LIGHTHOUSE_PREFERENCE;

      return await fetchWrapper<RevenueLighthousePreferenceResponse>(url);
    },
    {
      enabled,
    },
  );

  return {
    ...result,
    tenantId: window.localStorage.getItem('ZRevenue_TenantID'),
    userId: window.localStorage.getItem('ZRevenue_UserID'),
  };
};

export const useLighthousePreference = (
  { variant: variantOverride }: NavigationOverrides = {},
  identityQueryKey = IDENTITY_QUERY_KEY,
) => {
  const variant = useNavigationVariant({ variant: variantOverride });
  const isBilling = variant === 'billing';
  const isRevenue = variant === 'revenue';
  const {
    isLighthouseConfigEnabled,
    isLoading: isBillingLighthousePreferenceLoading,
    isError: isBillingLighthousePreferenceError,
    tenantId: billingTenantId,
    userId: billingUserId,
  } = useBillingLighthousePreference(isBilling, identityQueryKey);
  const {
    data: revenueLighthousePreference,
    isLoading: isRevenueLighthousePreferenceLoading,
    isError: isRevenueLighthousePreferenceError,
    tenantId: revenueTenantId,
    userId: revenueUserId,
  } = useRevenueLighthousePreference(isRevenue);

  return {
    isLighthouseConfigEnabled: isBilling
      ? isLighthouseConfigEnabled
      : revenueLighthousePreference?.globalNav,
    isLoading:
      isBillingLighthousePreferenceLoading ||
      isRevenueLighthousePreferenceLoading,
    isError:
      isBillingLighthousePreferenceError || isRevenueLighthousePreferenceError,
    tenantId: isBilling ? billingTenantId : revenueTenantId,
    userId: isBilling ? billingUserId : revenueUserId,
  };
};
